import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core'

import { FormControl, ReactiveFormsModule } from '@angular/forms'

import { TextWidget } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
@Component({
  selector: 'gb-presenter-widget-text',
  imports: [ReactiveFormsModule],
  templateUrl: './gb-presenter-widget-text.component.html',
  styleUrl: './gb-presenter-widget-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetTextComponent {
  @Input({ required: true }) textWidget!: TextWidget

  @Input() editable = false

  @Output() textCommitted = new EventEmitter<string>()
  @Output() editFinished = new EventEmitter<void>()

  @ViewChild('editor') private editor?: ElementRef<HTMLTextAreaElement>

  isEditing = false
  readonly editControl = new FormControl<string>('', { nonNullable: true })

  constructor(
    private colorResolver: GbPresenterColorResolverService,
    private hostEl: ElementRef<HTMLElement>,
  ) {}

  get fontFamily(): string {
    return this.textWidget?.font_family || this.colorResolver.globalFontFamily() || 'Arial, sans-serif'
  }

  get fontSize(): number {
    return (this.textWidget?.font_size ?? 0) / 8
  }

  get textColor(): string {
    return this.colorResolver.resolve(this.textWidget?.color) || this.colorResolver.resolvedGlobalColor() || '#000000'
  }

  startEdit(event: MouseEvent): void {
    if (!this.editable) {
      return
    }
    event.stopPropagation()

    this.isEditing = true
    this.editControl.setValue(this.textWidget?.text ?? '')

    queueMicrotask(() => {
      this.editor?.nativeElement?.focus()
      this.editor?.nativeElement?.select()
    })
  }

  cancelEdit(event?: Event): void {
    event?.stopPropagation()
    this.isEditing = false
  }

  commitEdit(event?: Event): void {
    event?.stopPropagation()

    const nextText = this.editControl.value
    this.isEditing = false
    this.textCommitted.emit(nextText)
  }

  onEditorKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelEdit(event)
      return
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.commitEdit(event)
      this.editFinished.emit()
    }
  }

  stopEvent(event: Event): void {
    event.stopPropagation()
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isEditing) {
      return
    }

    const targetNode = event.target as Node | null
    if (!targetNode) {
      return
    }

    if (!this.hostEl.nativeElement.contains(targetNode)) {
      this.commitEdit(event)
    }
  }
}
